/**
 * Whether the app currently believes it has a connection.
 *
 * `navigator.onLine` is the signal, and it is **only ever a hint**: it reports a
 * network interface, not reachability, so a walk-in cooler with a WiFi radio in
 * range and no route reads as online. That is exactly the case this app cares
 * about, so nothing here is allowed to be authoritative — the flush attempts and a
 * failure keeps the draft `pending`. The value drives what the header *says*, and
 * the request outcome drives what the app *believes*.
 *
 * The `online` event is what triggers a reconnect flush. It fires on a real
 * interface change, which is the cheap majority of recoveries; a flush also runs
 * when the count screen is re-focused, because coming back to the app is the other
 * moment a manager expects their queue to move.
 */
import { useEffect, useState } from "react";

export function useOnline(): boolean {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === "undefined" ? true : navigator.onLine !== false,
  );

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  return online;
}
